'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { scriptConfig } from '@headless-workspace/config';
import { ClientEnvKey, useAppLocale, useClientData } from '@headless-workspace/domain/common/client';
import { GlowSkeleton } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { LanguageHelpers } from '@headless-workspace/utils';
import { useScript } from 'usehooks-ts';
import { PlayerOptions } from './PlayerOptions';
import styles from './ekoo-custom.module.css';
import { useAudioPlayer } from './useAudioPlayer';

type ProductDetailAudioContainerProps = {
    masterProductId: string;
};

const AUDIO_CONTAINER_ID = 'standalone-audio-player';

const TIME_OUT_MS = 1000;

export const ProductDetailAudioContainer: Component<ProductDetailAudioContainerProps> = ({ masterProductId }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [status, setStatus] = useState<'loading' | 'hasAudio' | 'noAudio'>('loading');

    const locale = useAppLocale();

    const t = useTranslations('Discover.Pdp.information.audio');

    const { getClientEnvVariable } = useClientData();

    useScript(scriptConfig.AudioPlayer.url, {
        removeOnUnmount: true,
        id: scriptConfig.AudioPlayer.id,
    });

    useAudioPlayer();

    useEffect(() => {
        window.ekooOptions = PlayerOptions(
            LanguageHelpers.parseLanguage(locale),
            AUDIO_CONTAINER_ID,
            masterProductId,
            t('mainText'),
            t('secondaryText'),
            getClientEnvVariable(ClientEnvKey.EkooWebsiteId),
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const mutation = new MutationObserver(() => {
            if (containerRef.current && containerRef.current.children.length > 0) {
                setStatus('hasAudio');
                mutation.disconnect();
            }
        });

        const timeoutId = setTimeout(() => {
            if (containerRef.current && containerRef.current.children.length === 0) {
                if (status === 'loading') {
                    setStatus('noAudio');
                }
                clearTimeout(timeoutId);
            }
        }, TIME_OUT_MS);

        if (containerRef.current) {
            mutation.observe(containerRef.current, { childList: true });
        }

        return () => {
            mutation.disconnect();
            clearTimeout(timeoutId);
        };
    }, [status]);

    if (status === 'noAudio') {
        return null;
    }

    return (
        <div
            id={'ekoo-container'}
            className={clsx(
                'w-full tablet:w-[343px]',
                status === 'loading'
                    ? 'border-none hover:border-none'
                    : 'border-medium border-solid border-border-primary hover:border-bold hover:border-border-selected',
                styles.audioWrapper,
            )}
        >
            <GlowSkeleton
                containerClassName={clsx({ hidden: status !== 'loading' }, 'flex-1 w-full h-full')}
                itemClassName={'rounded-3.5'}
            />
            <div
                id={AUDIO_CONTAINER_ID}
                ref={containerRef}
                className={clsx({ hidden: status !== 'hasAudio' }, styles.ekooAudioContainer)}
            />
        </div>
    );
};
